import { FC } from 'react';
import { FiSettings } from 'react-icons/fi';
import { useParams } from 'react-router-dom';
import { NavPage } from '../../components/page/Breadcrumbs';
import { PageContainer } from '../../components/page/PageContainer';
import { PageHeader } from '../../components/page/PageHeader';
import { PageSizer, SizerMode } from '../../components/page/PageSizer';
import { isAdmin, useAuthenticationService } from '../../services/authentication/authentication-service';
import { RequireAssignedLocation } from '../../services/authentication/routes/RequireAssignedLocation';
import { Feature } from '../../services/backstage/Feature';
import { handleError } from '../../services/gql/config/URQLProvider';
import { Location, useGetLocationsQuery } from '../../services/gql/graphql.generated';
import { useLocationService } from '../../services/location/LocationService';
import { ClockInDevicesCard } from './ClockInDevicesCard';
import { DeleteLocationCard } from './DeleteLocationCard';
import { LocationDetailsCard } from './LocationDetailsCard';
import strings from './manageLocation.translations';

export const ManageLocationPage: FC = () => {
  const { locationId: urlLocationId } = useParams<'locationId'>();
  const selectedLocationId = useLocationService(state => state.selectedLocation?.id);
  const [role] = useAuthenticationService(state => [state.role]);

  const [{ data: locationsData, error: locationsError }] = useGetLocationsQuery();
  const location = locationsData?.locations?.find(location => location.id === Number(urlLocationId));
  if (locationsError) handleError(locationsError);

  const breadcrumbs: NavPage[] = [
    {
      name: strings.manageOrgTitle,
      to: '/manage-organisation'
    },
    {
      name: location?.name ?? '', // Keep the breadcrumb label empty until the location name has been fetched.
      to: `/manage-location/${urlLocationId}`
    }
  ];

  const canDeleteLocation = isAdmin(role) && Number(urlLocationId)! !== selectedLocationId;

  return (
    <PageContainer
      customHeader={
        <PageHeader pageTitle={location?.name ?? strings.title} breadcrumbs={breadcrumbs} icon={<FiSettings />} />
      }
    >
      <PageSizer className="flex flex-col gap-5" mode={SizerMode.PAGE}>
        <RequireAssignedLocation locationIds={[urlLocationId!]} errorMessage={strings.notAssigned}>
          <LocationDetailsCard location={location as Location} />
          <ClockInDevicesCard />
          <Feature name="debug">
            {canDeleteLocation && <DeleteLocationCard locationId={Number(urlLocationId)!} />}
          </Feature>
        </RequireAssignedLocation>
      </PageSizer>
    </PageContainer>
  );
};
